import React, { useState, useEffect } from "react";
import { useComponentsStore } from "../../Stores/componentsStore";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "../../Stores/authStore";
import { useVersionChangeStore, getCalculatedTimeLeft } from "../../Stores/versionChangeStore";
import devDebug from "../../Helpers/debug";
import { removeAllLocalState } from "../../Services/auth";
import { useTenderStrategyStore } from "../../Stores/tenderStrategyStore";

const componentFetchVersionJson = async () => {
  try {
    const response = await fetch(`/version.json?t=${new Date().getTime()}`);
    if (!response.ok) {
      devDebug("Failed to fetch version info (RefreshNotification component):", response.statusText);
      return null;
    }
    const data = await response.json();
    return data;
  } catch (error) {
    devDebug("Error fetching version info (RefreshNotification component):", error);
    return null;
  }
};

export default function RefreshNotification() {
  const { t } = useTranslation();
  const showRefreshWarningWithLogout = useComponentsStore((state) => state.showRefreshWarningWithLogout);
  const userID = useAuthStore((state) => state.user?.id);

  // Subscribe to timer details for real-time updates
  const { timerStartTime, initialTimerDuration } = useVersionChangeStore((state) => ({
    timerStartTime: state.timerStartTime,
    initialTimerDuration: state.initialTimerDuration,
  }));

  const [timeLeftForDisplay, setTimeLeftForDisplay] = useState(0);

  useEffect(() => {
    if (timerStartTime && initialTimerDuration) {
      // Calculate initial timeLeft immediately
      setTimeLeftForDisplay(getCalculatedTimeLeft({ timerStartTime, initialTimerDuration }));

      // Update timeLeft every second for display
      const intervalId = setInterval(() => {
        const currentRemaining = getCalculatedTimeLeft({
          timerStartTime: useVersionChangeStore.getState().timerStartTime, // Get fresh values
          initialTimerDuration: useVersionChangeStore.getState().initialTimerDuration,
        });
        setTimeLeftForDisplay(currentRemaining);
        if (currentRemaining <= 0) {
          // The main hook should handle the reload. This just stops updating display.
          clearInterval(intervalId);
        }
      }, 1000);
      return () => clearInterval(intervalId);
    } else {
      setTimeLeftForDisplay(0); // No active timer
    }
  }, [timerStartTime, initialTimerDuration]);

  const handleAddTwoMinutes = () => {
    const currentState = useVersionChangeStore.getState();
    if (currentState.timerStartTime && currentState.initialTimerDuration) {
      const newInitialDuration = currentState.initialTimerDuration + 120;
      devDebug(`Adding 2 minutes. Old duration: ${currentState.initialTimerDuration}, New: ${newInitialDuration}`);
      // We keep the original startTime, just extend the total duration
      useVersionChangeStore.getState().setTimerDetails(currentState.timerStartTime, newInitialDuration);
    }
  };

  // New handler for "Refresh now" button
  const handleRefreshNowClick = async () => {
    devDebug("Refresh Now button clicked in RefreshNotification component");

    // 1. Stop all timers
    const vcsState = useVersionChangeStore.getState();
    if (vcsState.localTimerId) {
      clearInterval(vcsState.localTimerId);
      useVersionChangeStore.getState().setLocalTimerId(null);
      devDebug("Refresh Now (Component): Cleared local timer ID:", vcsState.localTimerId);
    }
    useVersionChangeStore.getState().clearTimerDetails();
    devDebug("Refresh Now (Component): Cleared global timer details from store.");

    // 2. Hide the notification (set flags to false)
    useComponentsStore.getState().setShowRefreshWarningWithLogout(false);
    useComponentsStore.getState().setShowRefreshWarningWithoutLogout(false);
    devDebug("Refresh Now (Component): Set notification warning flags to false in store.");

    // 3. Set local data versions to be the same as server data versions
    const versionData = await componentFetchVersionJson(); // Using the duplicated function
    devDebug("Refresh Now (Component) -> Fetched latest versionData from server: ", versionData);
    if (versionData) {
      useVersionChangeStore.getState().setLoginDataVersion(versionData.loginDataVersion);
      useVersionChangeStore.getState().setOtherDataVersion(versionData.otherDataVersion);
      devDebug("Refresh Now (Component): Updated local versions in versionChangeStore.");
    } else {
      devDebug(
        "Refresh Now (Component): Failed to fetch version data. Store versions not updated by Refresh Now button."
      );
    }

    // 4. Replicate parts of handleTimerFinished from the hook (user state management, then reload)
    const { user } = useAuthStore.getState();
    // `showRefreshWarningWithLogout` (the component's const) determines if full logout is needed.
    // This prop reflects the reason the notification was shown.

    if (user?.id) {
      if (showRefreshWarningWithLogout) {
        // This is the const `showRefreshWarningWithLogout` from line 30
        devDebug("Refresh Now (Component) -> User needs logout. Performing full local state removal (including auth).");
        removeAllLocalState(true); // Logout user (true means remove auth data)
      } else {
        devDebug("Refresh Now (Component) -> User needs refresh without logout. Resetting non-auth local state.");
        removeAllLocalState(false); // Keep auth, reset other data (false means keep auth data)
        useAuthStore.getState().setStrategiesNeverRequested(true);
        useTenderStrategyStore.getState().setStrategiesStatus("loading");
        useTenderStrategyStore.getState().setTriggerStrategyReload();
      }
    } else {
      devDebug("Refresh Now (Component) -> No user ID found. Not touching local state removal logic.");
    }

    devDebug("Refresh Now (Component) -> Operations complete. Reloading window...");
    window.location.reload();
  };

  const notificationPartOne = showRefreshWarningWithLogout
    ? "A new version is available. Please logout and back in to get the latest version."
    : "A new version is available. Please refresh the page.";
  const notificationPartTwo = "If you have any unsaved strategies, please make sure to save them first.";
  const notificationPartThree = showRefreshWarningWithLogout ? "logoutText" : "timerText";

  if (!userID || (!showRefreshWarningWithLogout && !useComponentsStore.getState().showRefreshWarningWithoutLogout)) {
    return null; // Don't show if no user or no relevant warning is active
  }

  // Only show if a timer is actually running (indicated by timerStartTime)
  if (!timerStartTime) {
    return null;
  }

  return (
    <div className="trial_notification d-flex flex-column tc-flex-gap-5px align-items-start align-items-sm-center">
      <div className="text-start text-sm-center">{t(notificationPartOne)}</div>
      <div className="text-start text-sm-center">{t(notificationPartTwo)}</div>
      <div className="d-flex tc-flex-gap-5px text-start text-sm-end">
        <div>{t(notificationPartThree, { timeLeft: timeLeftForDisplay })}</div>
        <button
          onClick={handleAddTwoMinutes}
          className="tc-small-notification-button btn-secondary"
          disabled={timeLeftForDisplay === 0} // Disable if timer has run out
        >
          {t("Add 2 minutes")}
        </button>
        <button
          onClick={handleRefreshNowClick} // Use the new handler
          className="tc-small-notification-button btn-secondary"
          disabled={timeLeftForDisplay === 0} // Disable if timer has run out
        >
          {t("Refresh now")}
        </button>
      </div>
    </div>
  );
}
